import React from "react";
import { useGetOnboardingFunnel } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Check, Minus, TrendingDown } from "lucide-react";

function formatAgentType(type?: string) {
  if (!type) return "—";
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function OnboardingFunnel() {
  const { data, isLoading, error } = useGetOnboardingFunnel();

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/3 rounded mb-6"></div>
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !data)
    return (
      <div className="p-6 text-red-500">Failed to load onboarding funnel.</div>
    );

  const maxCount = Math.max(1, ...data.stages.map((s) => s.count));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-[#0B132B]">Onboarding Funnel</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total leads in funnel" value={String(data.totalLeads)} />
        <KpiCard
          label="Session attendance rate"
          value={`${data.sessionAttendanceRate}%`}
          accent="#00A859"
        />
        <KpiCard
          label="Onboarding-start rate"
          value={`${data.onboardingStartRate}%`}
        />
        <KpiCard
          label="Fully approved"
          value={String(data.approvedCount)}
          accent="#00A859"
        />
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            Conversion by stage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.stages.map((stage) => (
            <div key={stage.key} className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-[#0B132B]">
                  {stage.label}
                </span>
                <span className="flex items-center gap-3">
                  <span className="font-semibold text-[#0B132B]">
                    {stage.count}
                  </span>
                  {stage.dropOffPct > 0 && (
                    <span className="flex items-center gap-1 text-xs text-red-600">
                      <TrendingDown className="w-3 h-3" />
                      {stage.dropOffPct}%
                    </span>
                  )}
                </span>
              </div>
              <div className="h-3 w-full rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#E6007E]"
                  style={{
                    width: `${Math.max(2, (stage.count / maxCount) * 100)}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">Leads</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Current stage</TableHead>
                <TableHead className="text-center">Invited</TableHead>
                <TableHead className="text-center">Attended</TableHead>
                <TableHead className="text-center">Started</TableHead>
                <TableHead className="text-center">FICA</TableHead>
                <TableHead>Last activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.leads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell className="font-medium">
                    {lead.applicantName}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatAgentType(lead.agentType)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={lead.status} />
                  </TableCell>
                  <TableCell className="text-center">
                    <Tick on={lead.invited} />
                  </TableCell>
                  <TableCell className="text-center">
                    <Tick on={lead.attended} />
                  </TableCell>
                  <TableCell className="text-center">
                    <Tick on={lead.started} />
                  </TableCell>
                  <TableCell className="text-center">
                    <Tick on={lead.ficaComplete} />
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {lead.submittedAt
                      ? new Date(lead.submittedAt).toLocaleDateString()
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}
              {data.leads.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center p-8 text-gray-500"
                  >
                    No leads in the funnel yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  label,
  value,
  accent = "#0B132B",
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <Card className="border-gray-100 shadow-sm">
      <CardContent className="p-4">
        <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
        <p className="text-2xl font-bold" style={{ color: accent }}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function Tick({ on }: { on: boolean }) {
  return on ? (
    <Check className="w-4 h-4 text-[#00A859] inline" />
  ) : (
    <Minus className="w-4 h-4 text-gray-300 inline" />
  );
}
